import React, { useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';
import DrawSvg from '../elements/DrawSvg';
import RoadmapItem from '../elements/RoadmapItem';
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { Title } from "../../styles/shared";
import backgroundImage from '../../assets/bg2.png'; // Update the path to your image

const StyledRoadmap = styled.section`
  min-height: 100vh;
  min-height: 100svh;
  width: 100vw;
  background: url(${backgroundImage}) no-repeat center center / cover;
  background-color: ${props => props.theme.body}; // Optional, in case the image doesn't load
  background-size: cover; // Ensures the image covers the entire section
  background-position: center; // Centers the image
  background-repeat: no-repeat; // Prevents the image from repeating
`;

const Container = styled.div`
  width: 70%;
  min-height: 100vh; // Ensures it fills the viewport but grows with content
  // background-color: ${props => props.theme.body};
  display: flex;
  margin: auto;
  justify-content: center;
  align-items: center;
  position: relative;
  flex-direction: column; // Aligns Items vertically
  @media (max-width: 64em) {
    width: 80%;
  }
  @media (max-width: 48em) {
    width: 90%;
  }
`;

const SvgContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Items = styled.ul`
  list-style: none;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  &>*:nth-of-type(2n + 1) {
    justify-content: start;
    div {
      border-radius: 50px 0 50px 0;
      text-align: right;
    }
    p {
      border-radius: 40px 0 40px 0;
    }
    @media (max-width: 48em) {
      justify-content: center;
      div {
        border-radius: 0 50px 0 50px;
        text-align: left;
      }
      p {
        border-radius: 0 40px 0 40px;
      }
    }
    @media (max-width: 30em) {
      div {
        border-radius: 0 40px 0 40px;
        text-align: left;
      }
      p {
        border-radius: 0.3rem 0.6rem 0.6rem 1.2rem;
      }
    }
  }
  &>*:nth-of-type(2n) {
    justify-content: end;
    div {
      border-radius: 0 50px 0 50px;
      text-align: left;
    }
    p {
      border-radius: 0 40px 0 40px;
    }
    @media (max-width: 48em) {
      justify-content: center;
      border-radius: 50px 0 50px 0;
      text-align: right;
    }
  }

  @media (max-width: 48em) {
    width: 90%;
  }
`;

const Item = styled.li`
  width: 100%;
  height: 100%;
  display: flex;

  @media (max-width: 48em) {
    justify-content: flex-end !important;
  }
`;

function Roadmap() {
  const revealRefs = useRef([]);
  revealRefs.current = [];
  gsap.registerPlugin(ScrollTrigger);
  
  const addToRefs = el => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  };

  useLayoutEffect(() => {
    let t1 = gsap.timeline();
    if (revealRefs.current) {
      revealRefs.current.forEach((el, index) => {
        t1.fromTo(
          el.childNodes[0],
          { y: "0" },
          {
            y: "-30%",
            scrollTrigger: {
              id: `section-${index + 1}`,
              trigger: el,
              start: 'top center+=200px',
              end: "bottom bottom",
              scrub: true,
              // markers: true
            }
          }
        );
      });
    }
  }, []);

  return (
    <StyledRoadmap id="roadmap">
      <Title mode="light" style={{ marginBottom: "90px" }}>
        Roadmap
      </Title>
      <Container>
        <SvgContainer>
          <DrawSvg />
        </SvgContainer>
        <Items>
          <Item>&nbsp;</Item>
          <RoadmapItem addToRefs={addToRefs} title="Explore Topics and Interests" subTitle="Upon their first visit, students are welcomed to AirEdify, where they can explore various fields of study, topics, and skill-building areas aligned with their personal interests and career goals." />
          <RoadmapItem addToRefs={addToRefs} title="Personalized Learning Roadmaps" subTitle="Once students select their areas of interest, they are guided through personalized learning roadmaps tailored to each topic. These roadmaps include structured tutorials, video content, and notes to ensure a comprehensive learning experience." />
          <RoadmapItem addToRefs={addToRefs} title="Access Tutorials and Notes" subTitle="Students begin learning through our curated library of tutorials and notes, which connect seamlessly with their roadmap. This library includes both theoretical and practical resources to support well-rounded skill development." />
          <RoadmapItem addToRefs={addToRefs} title="Engage in Certified Masterclasses" subTitle="To deepen their knowledge, students can attend live certified masterclasses led by industry experts. These classes offer hands-on learning and provide certifications that enhance their resumes." />
          <RoadmapItem addToRefs={addToRefs} title="Build and Optimize Resumes" subTitle="AirEdify’s resume builder guides students through creating industry-standard resumes, with ATS optimization checks to ensure job application success. Students can continuously update and refine their resumes to reflect their growing skillsets." />
          <RoadmapItem addToRefs={addToRefs} title="Apply for Internships and Events" subTitle="You can browse and apply for internship opportunities, job listings, hackathons, and industry events directly from the platform. This integration allows them to put their skills into action and gain real-world experience." />
          <RoadmapItem addToRefs={addToRefs} title="Join Interest-Based Communities" subTitle="Students can connect with peers and join communities centered around shared interests, fostering collaboration, mentorship, and mutual support for continuous growth." />
          <RoadmapItem addToRefs={addToRefs} title="Participate in Extracurricular Activities" subTitle="AirEdify notifies students about upcoming extracurricular events such as contests, music and dance competitions, sports activities, and arts events, supporting their holistic development." />
          <RoadmapItem addToRefs={addToRefs} title="Stay Motivated with Streak Rewards" subTitle="AirEdify’s Streak Rewards keep students motivated by rewarding regular learning activity with exclusive perks like skill badges, masterclass access, and premium content. This gamified system makes learning fun, encouraging long-term engagement and habit-building." />
        </Items>
      </Container>
    </StyledRoadmap>
  );
}

export default Roadmap;
